import FormInputRadioCheckbox from 'components/FormAddTransaction/components/FormInputRadioCheckbox';
import SizeBox from 'components/common/SizeBox';
import withAddPaytoAccountsAgreement from 'features/to/HOCs/payment/withAddPaytoAccountsAgreement';
import withDeletePaytoAccountAgreements from 'features/to/HOCs/payment/withDeletePaytoAccountAgreements';
import withEditPaytoAccountAgreements from 'features/to/HOCs/payment/withEditPaytoAccountAgreements';
import withWatchPaytoGuidelineOptional from 'features/to/HOCs/payment/withWatchPaytoGuidelineOptional';
import {
  PAYTO_ACCOUNT_ACTIVE_STATUS,
  PAYTO_ACCOUNT_CREATED_STATUS,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import usePaytoGuideline from 'features/to/hooks/usePaytoGuideline';
import { format } from 'helpers';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useCommonStore from 'hooks/useCommonStore';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import React from 'react';
import styled from 'styled-components';
import {
  cursorPointerMixin,
  displayFlexRowCenterCenterMixin,
  displayFlexRowCenterEndMixinObject,
  fontSize12Mixin,
  fontSize12MixinObject,
  fontSize14Mixin,
  paragraph500Mixin,
} from 'styles';
import { domUtils } from 'utils';

const PaytoAccountAgreementItem = withAddPaytoAccountsAgreement(
  withEditPaytoAccountAgreements(
    withDeletePaytoAccountAgreements(
      withWatchPaytoGuidelineOptional(
        ({
          index,
          data,
          onSelect,
          fetchPaytoAccountsAgreementLatest,
          onEdit,
          onDelete,
          onShowWatchPaytoGuideline,
        }) => {
          const { t } = useLang();
          const { paytoAccountInformation, setPaytoAccountAgreementEditing } =
            usePaytoStore();
          const { setLoading } = useCommonStore();
          const { isAddTransactionPage } = usePage();
          const { getTotalToPay } = useAddTransaction();
          const { getTotalPayAmount } = useFxFormAddTransactionStore();
          const { getPaytoGuidelineUrl } = usePaytoGuideline();

          let {
            paymentAgreementUID,
            accountNumber,
            paymentAgreementStatus,
            bsb,
            maxAmount,
            blocked,
            pendingAmend,
            bankCode,
            responseBy,
            payId,
            bankName,
          } = data || {};

          const { id } = paytoAccountInformation || {};
          const isSelected = id === paymentAgreementUID;
          const isDisabled = false;
          const bankCodeAccountNumberString = `${
            bankName || bankCode || ''
          }${accountNumber}`;
          const payIdAccountNumberString = `${t('label_payid')} (${payId})`;
          const maxAmountString = `${t(
            'label_max_amount'
          )}: AUD ${format.toAmountCentStr(maxAmount)}`;
          const totalToPay = isAddTransactionPage
            ? getTotalToPay()
            : getTotalPayAmount();

          const isActive =
            paymentAgreementStatus === PAYTO_ACCOUNT_ACTIVE_STATUS;
          const isCreated =
            paymentAgreementStatus === PAYTO_ACCOUNT_CREATED_STATUS;

          const isNotAllowSelect = [
            isCreated,
            isActive && Number(maxAmount) < totalToPay,
            blocked,
          ].includes(true);

          const handleOnClick = () => {
            if (isNotAllowSelect) return;
            onSelect(data);
          };

          const handleOnEdit = () => {
            setPaytoAccountAgreementEditing(true);
            onEdit(data);
          };

          const handleOnDelete = () => {
            onDelete(data);
          };

          const handleHowToCTAOnClick = () => {
            console.log(
              `⭐ PaytoAccountAgreementItem 👉 handleHowToCTAOnClick ➡️ CLICK ↩`
            );
            // onShowWatchPaytoGuideline({
            //   isAgreementByBsb: !payId,
            // });
            window.open(getPaytoGuidelineUrl(data), '_blank');
          };

          const handleRefreshCTAOnClick = (e) => {
            console.log(
              `⭐ PaytoAccountAgreementItem 👉 handleRefreshCTAOnClick ↩`
            );
            if (e?.target?.className?.includes('how-to-cta')) {
              handleHowToCTAOnClick();
              return;
            }
            setLoading(true);
            fetchPaytoAccountsAgreementLatest(() => {
              setLoading(false);
              if (pendingAmend) return;
              onSelect();
            });
          };

          const handleEditPaytoCTAOnClick = (e) => {
            console.log(
              `⭐ PaytoAccountAgreementItem 👉 handleEditPaytoCTAOnClick ↩`
            );
            if (e?.target?.className?.includes('edit-payto-cta')) {
              handleOnEdit();
              return;
            }
          };

          const handleOnClickCancelAuthorizationButton = () => {
            handleOnDelete();
          };

          const renderCheckboxSection = () => {
            return (
              <GridItem $isDisabled={isDisabled} onClick={handleOnClick}>
                <FormInputRadioCheckbox
                  isChecked={isSelected}
                  styles={{
                    paddingLeft: '32px',
                    width: '24px',
                  }}
                  isNotAllowSelect={isNotAllowSelect}
                />
              </GridItem>
            );
          };

          const renderBankCodeAccountNumber = () => {
            if (payId) return <Text>{payIdAccountNumberString}</Text>;
            return <Text>{bankCodeAccountNumberString}</Text>;
          };
          const renderInformationSection = () => {
            if (isActive && pendingAmend) {
              return (
                <GridItem $isDisabled={isDisabled}>
                  {renderBankCodeAccountNumber()}
                  <SubText>{maxAmountString}</SubText>
                  <SubText style={{ color: 'var(--bg-primary)' }}>
                    {domUtils.decodeHtmlEntities(
                      t('label_account_agreement_item_pending_amend_status', {
                        datetime: responseBy,
                      })
                    )}
                  </SubText>
                  <CTAText
                    dangerouslySetInnerHTML={{
                      __html: t('label_account_agreement_item_cta_2', {
                        index,
                      }),
                    }}
                    onClick={handleRefreshCTAOnClick}
                  />
                </GridItem>
              );
            }
            if (isCreated) {
              return (
                <GridItem $isDisabled={isDisabled}>
                  {renderBankCodeAccountNumber()}
                  <SubText style={{ color: 'var(--bg-primary)' }}>
                    {domUtils.decodeHtmlEntities(
                      t('label_account_agreement_item_pending_status', {
                        datetime: responseBy,
                      })
                    )}
                  </SubText>
                  <SizeBox height={4} />
                  <CancelAuthorizationButton
                    onClick={handleOnClickCancelAuthorizationButton}
                  >
                    {t('label_cancel_authorization')}
                  </CancelAuthorizationButton>
                  <SizeBox height={4} />
                  <CTAText
                    dangerouslySetInnerHTML={{
                      __html: t('label_account_agreement_item_cta_1', {
                        index,
                      }),
                    }}
                    onClick={handleHowToCTAOnClick}
                  />
                </GridItem>
              );
            }
            if (Number(maxAmount) < totalToPay)
              return (
                <GridItem $isDisabled={isDisabled} onClick={handleOnClick}>
                  {renderBankCodeAccountNumber()}
                  <SubText
                    style={{
                      color: 'var(--ds-c-red)',
                    }}
                  >
                    {maxAmountString}
                  </SubText>
                  <SubText
                    style={{
                      ...fontSize12MixinObject,
                      color: 'var(--ds-c-red)',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t('label_edit_and_increase_max_amount'),
                    }}
                    onClick={handleEditPaytoCTAOnClick}
                  />
                </GridItem>
              );
            return (
              <GridItem $isDisabled={isDisabled} onClick={handleOnClick}>
                {renderBankCodeAccountNumber()}
                <SubText>{maxAmountString}</SubText>
              </GridItem>
            );
          };

          const renderDeleteButton = () => {
            // import DeleteIcon from 'assets/ver2/icons/trash-icon.svg';
            // return <DeleteButton onClick={handleOnDelete} src={DeleteIcon} />;
            return null;
          };
          const renderEditButton = () => (
            <EditButton onClick={handleOnEdit}>{t('button_edit')}</EditButton>
          );
          const renderActionsSection = () => {
            if (isActive && pendingAmend)
              return (
                <GridItem
                  $isDisabled={isDisabled}
                  style={displayFlexRowCenterEndMixinObject}
                >
                  {renderDeleteButton()}
                </GridItem>
              );
            if (isCreated)
              return (
                <GridItem
                  $isDisabled={isDisabled}
                  style={displayFlexRowCenterEndMixinObject}
                >
                  {renderDeleteButton()}
                </GridItem>
              );
            return (
              <GridItem
                $isDisabled={isDisabled}
                style={displayFlexRowCenterEndMixinObject}
              >
                {renderEditButton()}
                {renderDeleteButton()}
              </GridItem>
            );
          };

          return (
            <PaytoAccountAgreementItemStyled
              $isActive={isSelected}
              $isDisabled={isDisabled}
            >
              {renderCheckboxSection()}
              {renderInformationSection()}
              {renderActionsSection()}
            </PaytoAccountAgreementItemStyled>
          );
        }
      )
    )
  )
);

const PaytoAccountAgreementItemStyled = styled.div`
  display: grid;
  grid-template-columns: 32px calc(100% - 112px) 64px;
  grid-gap: 8px;
  width: 100%;
  height: fit-content;
  padding: 10px;
  margin-bottom: 0px;
  border-bottom: 1px solid var(--ds-bg-2);
  background-color: transparent;

  ${(props) =>
    props.$isDisabled &&
    `
    background-color: transparent;
    color: #7a8189;
  `};

  &:last-child {
    border-bottom: none;
  }
`;

const GridItem = styled.div`
  width: 100%;
  display: grid;
  align-items: center;
  justify-content: start;

  &:nth-child(1) {
    justify-content: center;
    align-items: center;
  }

  &:last-child {
    justify-content: space-around;
    align-items: start;
  }

  & div {
    & div,
    input {
      cursor: ${(props) => props.$isDisabled && 'not-allowed'};
    }
  }

  & p {
    cursor: ${(props) => props.$isDisabled && 'not-allowed'};
  }
`;

const Text = styled.p`
  ${paragraph500Mixin};
  font-weight: 700;
  text-align: left;
`;
const SubText = styled.p`
  ${paragraph500Mixin};
  text-align: left;

  & span.edit-payto-cta {
    color: var(--ds-c-blue-hyperlink-default);
    text-decoration: underline;
    ${cursorPointerMixin};
  }
`;
const CTAText = styled.p`
  ${paragraph500Mixin};
  ${fontSize12Mixin};
  text-align: left;
  pointer-events: none;

  & span:nth-child(1) {
    color: #002ff1;
    text-decoration: underline;
    ${cursorPointerMixin};
    pointer-events: initial;
  }

  & span:nth-child(2) {
    color: #1aa758;
    text-decoration: underline;
    ${cursorPointerMixin};
    pointer-events: initial;
  }
`;

const EditButton = styled.div`
  ${paragraph500Mixin};
  ${fontSize14Mixin};
  color: var(--ds-c-blue-hyperlink-default);
  ${cursorPointerMixin};
`;
const DeleteButton = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const CancelAuthorizationButton = styled.div`
  ${cursorPointerMixin};
  ${paragraph500Mixin};
  ${displayFlexRowCenterCenterMixin};
  color: #565656;
  border-radius: 100px;
  border: 1px solid #565656;
  padding-inline: 6px;
  width: fit-content;
`;

export default PaytoAccountAgreementItem;
